import useGetFolderEntry from '@/api/queries/project/useGetFolderEntry';
import useGetProjectFolderEntries, {
  TFolderEntry,
} from '@/api/queries/project/useGetProjectFolderEntries';
import { GetFolderEntriesInput } from '@/gql/graphql';
import { AnchorButton, ButtonDropdown } from '@payaca/ambrosia';
import Lightbox, {
  TProps as TLightboxProps,
} from '@payaca/components/lightbox/Lightbox';
import * as documentActions from '@payaca/store/documents/documentActions';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import projectKeys from '../../../../../api/queries/project/keyFactory';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';

export type TProps = {
  projectId: string;
  folderEntryId?: string;
  onChange: (folderEntryId: string) => void;
  getFolderEntriesInputOpts: { input: GetFolderEntriesInput; enabled: boolean };
} & Pick<TLightboxProps, 'isOpen' | 'onClose'>;

const FilesPanelLightbox: FC<TProps> = (props) => {
  const {
    projectId,
    folderEntryId,
    onChange,
    isOpen,
    onClose,
    getFolderEntriesInputOpts,
  } = props;

  const {
    projectFolderEntries,
    hasPreviousPage,
    fetchPreviousPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useGetProjectFolderEntries(projectId, getFolderEntriesInputOpts);

  const folderEntryIndex = projectFolderEntries?.findIndex(
    (entry) => entry.id === folderEntryId
  );
  const isValidFolderEntryIndex =
    typeof folderEntryIndex === 'number' && folderEntryIndex >= 0;

  useEffect(() => {
    if (
      isValidFolderEntryIndex &&
      projectFolderEntries &&
      folderEntryIndex === projectFolderEntries.length - 1 &&
      hasNextPage &&
      !isFetching
    ) {
      void fetchNextPage();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'fetchNextPage'
  }, [
    isFetching,
    folderEntryIndex,
    hasNextPage,
    isValidFolderEntryIndex,
    projectFolderEntries,
  ]);

  const lightboxDetail: TLightboxProps['lightboxDetail'] =
    useGenerateLightboxDetails(
      projectId,
      isValidFolderEntryIndex
        ? projectFolderEntries?.[folderEntryIndex]
        : undefined
    );

  const nextSlideId =
    isValidFolderEntryIndex &&
    projectFolderEntries &&
    folderEntryIndex < projectFolderEntries.length - 1
      ? projectFolderEntries[folderEntryIndex + 1].id
      : undefined;

  const prevSlideId =
    isValidFolderEntryIndex && projectFolderEntries && folderEntryIndex > 0
      ? projectFolderEntries[folderEntryIndex - 1].id
      : false;

  return (
    <Lightbox
      isOpen={isOpen}
      onClose={onClose}
      onNext={
        nextSlideId
          ? () => {
              // If we're moving onto the last slide and there's more to load, load it
              if (
                folderEntryIndex === projectFolderEntries!.length - 2 &&
                hasNextPage &&
                !isFetching
              ) {
                void fetchNextPage();
              }

              onChange(nextSlideId);
            }
          : undefined
      }
      onPrev={
        prevSlideId
          ? () => {
              // If moving onto the first slide and there's more to load, load it
              if (folderEntryIndex === 1 && hasPreviousPage && !isFetching) {
                void fetchPreviousPage();
              }

              onChange(prevSlideId);
            }
          : undefined
      }
      lightboxDetail={lightboxDetail}
    />
  );
};

type LinkedEntityProps = {
  type: string;
  label?: string;
  to?: string;
};

const LinkedEntity: FC<LinkedEntityProps> = ({ type, label, to }) => {
  return (
    <div className="flex items-center">
      <UntitledIcon name="link-04.3" className="w-6 h-6 mr-2 text-gray-500" />
      <span className="mr-1">
        {type}
        {label ? ':' : ''}
      </span>
      {label && to && (
        <AnchorButton as={Link} to={to} variant="link">
          {label}
        </AnchorButton>
      )}
      {label && !to && <span>{label}</span>}
    </div>
  );
};

const MAX_LINKED_ENTITIES_TO_SHOW = 2;
const MAX_DROPDOWN_LINKED_ENTITIES_TO_SHOW_WITHOUT_SEARCH = 5;

const LinkedEntities: FC<{
  projectId: string;
  folderEntry: Extract<TFolderEntry, { __typename: 'UploadFolderEntry' }>;
}> = ({ folderEntry, projectId }) => {
  const history = useHistory();

  const linkedEntities = folderEntry.upload.linkedEntities.filter(
    // If we're here, we're already on the project page. No need to render
    // the project as a linked entity.
    (le) => le.__typename !== 'Project'
  );

  const linkedEntitiesProps: LinkedEntityProps[] = linkedEntities.map(
    (entity) => {
      switch (entity.__typename) {
        case 'Invoice': {
          return {
            type: 'Invoice',
            label: entity.customReference || entity.reference.toString(),
            to: `/projects/${projectId}/invoices/${entity.id}`,
          };
        }
        case 'Proposal': {
          return {
            type: 'Proposal',
            label: entity.customReference || entity.reference.toString(),
            to: `/quotes/${entity.id}`,
          };
        }
        case 'ChangeProposal': {
          return {
            type: 'Change proposal',
            label: entity.customReference || entity.reference.toString(),
            to: `/projects/${projectId}/changeproposals/${entity.privateId}`,
          };
        }
        case 'ScheduledEvent': {
          return {
            type: 'Scheduled event',
            label: entity.name,
            to: `/projects/${projectId}/schedule/${entity.id}`,
          };
        }
        case 'UnresolvedLinkedEntity': {
          return {
            type: entity.entityType,
          };
        }
        case 'PurchaseOrderIndirect':
        case 'PurchaseOrderCityPlumbing': {
          return {
            type: 'Purchase order',
            label: entity.stringReference,
            to: `/purchaseOrders/${entity.id}`,
          };
        }
      }
    }
  );

  // Add the upload source as another linked entity, even though it isn't
  // really one on the data model.
  if (folderEntry.upload?.source) {
    linkedEntitiesProps.push({
      type: 'Source',
      label: folderEntry.upload.source.description,
      to: folderEntry.upload.source.url ?? undefined,
    });
  }

  if (linkedEntitiesProps.length === 0) {
    return null;
  }

  return (
    <ul className="m-0 mt-2 p-0 list-none space-y-2">
      {linkedEntitiesProps
        .slice(0, MAX_LINKED_ENTITIES_TO_SHOW)
        .map((props, i) => (
          <li key={`linked-entity-${folderEntry.id}-${i}`}>
            <LinkedEntity {...props} />
          </li>
        ))}
      {linkedEntitiesProps.length > MAX_LINKED_ENTITIES_TO_SHOW && (
        <li>
          <ButtonDropdown
            variant="link"
            dropdownOptions={{
              // only has search if more than X linked entities to show
              hasSearch:
                linkedEntitiesProps.length - MAX_LINKED_ENTITIES_TO_SHOW >
                MAX_DROPDOWN_LINKED_ENTITIES_TO_SHOW_WITHOUT_SEARCH,
              items: linkedEntitiesProps
                .slice(MAX_LINKED_ENTITIES_TO_SHOW)
                .map(({ to, ...rest }) => ({
                  label: <LinkedEntity {...rest} />,
                  searchKey: rest.label,
                  onClick: () => {
                    if (to) {
                      history.push(to);
                    }
                  },
                })),
            }}
          >
            and {linkedEntitiesProps.length - MAX_LINKED_ENTITIES_TO_SHOW}{' '}
            more...
          </ButtonDropdown>
        </li>
      )}
    </ul>
  );
};

const useGenerateLightboxDetails = (
  projectId: string,
  folderEntry?: TFolderEntry
): TLightboxProps['lightboxDetail'] => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { project } = useGetProjectByIdOverview(projectId);
  const { folderEntry: fullFolderEntry } = useGetFolderEntry(
    projectId,
    folderEntry?.id,
    {
      enabled: folderEntry?.__typename === 'DocumentInstanceFolderEntry',
    }
  );
  const [isProcessing, setIsProcessing] = useState(false);

  if (!folderEntry) {
    return {
      type: 'loading',
    };
  }

  switch (folderEntry.__typename) {
    case 'UploadFolderEntry': {
      let type: TLightboxProps['lightboxDetail']['type'];

      if (folderEntry.upload.mimeType.startsWith('video/')) {
        type = 'video';
      } else if (folderEntry.upload.mimeType === 'application/pdf') {
        type = 'pdf';
      } else {
        type = 'image';
      }

      return {
        type,
        videoType: folderEntry.upload.mimeType,
        src: folderEntry.upload.url,
        thumbnailSrc: folderEntry.upload.thumbnailUrl || undefined,
        title: folderEntry.name,
        subtitle: (
          <LinkedEntities folderEntry={folderEntry} projectId={projectId} />
        ),
      };
    }
    case 'UrlFolderEntry':
      return {
        type: 'link',
        label: 'View external link',
        href: folderEntry.url.url,
        title: folderEntry.name,
        subtitle: null,
      };
    case 'DocumentFolderEntry': {
      if (folderEntry.document.completedAt) {
        return {
          type: 'link',
          label: 'View form',
          To: `/documents/${folderEntry.document.privateId}`,
          title: folderEntry.name,
          subtitle: null,
        };
      }

      if (folderEntry.document.__typename === 'DocumentFormPdf') {
        return {
          type: 'message',
          title: folderEntry.name,
          subtitle: null,
          children: (
            <>
              <p>
                This file is a PDF form. This type of form is no longer
                available. This record has been kept for auditing purposes only.
              </p>
            </>
          ),
        };
      }

      if (folderEntry.document.instance?.previewToken) {
        return {
          type: 'link',
          label: 'Continue form',
          To: `/forms/${folderEntry.document.instance?.previewToken}/menu?dealId=${project?.id}`,
          title: folderEntry.name,
          subtitle: null,
        };
      } else {
        return {
          type: 'link',
          label: 'Start form',
          onClick: () => {
            if (isProcessing) return;
            setIsProcessing(true);
            dispatch(
              documentActions.createFormInstanceForDocument.request({
                documentId: +folderEntry.document.privateId,
                callback: (formInstancePreviewToken: string) => {
                  setIsProcessing(false);
                  history.push(
                    `/forms/${formInstancePreviewToken}/menu?dealId=${project?.id}`
                  );
                  void queryClient.invalidateQueries({
                    queryKey: projectKeys.folderEntries(projectId),
                  });
                },
                onErrorCallback: () => {
                  setIsProcessing(false);
                },
              })
            );
          },
          title: folderEntry.name,
          subtitle: null,
          isProcessing,
        };
      }
    }
    case 'DocumentInstanceFolderEntry': {
      if (fullFolderEntry?.__typename !== 'DocumentInstanceFolderEntry') {
        return {
          type: 'loading',
        };
      }
      return {
        type: 'shadowDOM',
        title: folderEntry.name,
        subtitle: null,
        html: fullFolderEntry.documentInstance.documentHtml,
      };
    }
    default:
      return {
        type: 'loading',
      };
  }
};

export default FilesPanelLightbox;
